import { z } from 'zod'

import { ConceptSchema } from '~/@entities/Concept'
import { TrieKeySchema } from '~/@entities/Curriculum'
import { EqualityLevelSchema, ProblemRequestTypeSchema } from '~/@entities/MakeWorksheet'
import {
  PracticeTestOptionSchema,
  ProblemLevelSchema,
  ProblemTypeSchema,
} from '~/@entities/Problem'
import { SchoolTypeSchema } from '~/@entities/SchoolSystem'
import { ScoringResultSchema } from '~/@entities/Scoring'
import { StudentSchema } from '~/@entities/Student'
import { StudentWorksheetSchema } from '~/@entities/StudentWorksheet'
import { WorksheetProblemSchema } from '~/@entities/Worksheet'

// 오늘의 학습: 학생마다 매일 자동으로 출제되는 학습지와, 선생님이 그 출제 조건을 정하는 설정.
// 2026-10-14 백엔드 develop(6a0f8e7bb) 의 student/controller/response/DailyWorksheetUsed*Response.kt,
// worksheet/controller/response/DailyWorksheetFilterSettingResponse.kt·DailyWorksheetProblemCountResponse.kt,
// statistics/controller/response/DailyWorksheetStatistics*Response.kt,
// studentWorksheet/controller/response/DailyWorksheetScoringResponse.kt 와 각 model/type enum 으로 확인.

/***************** 설정 단위 *****************/
// 학생 한 명·학교급과 학년·반 중 무엇에 대한 설정인가
export const DAILY_LEARNING_SETTING_TYPE = ['STUDENT', 'GRADE', 'LESSON_CLASS'] as const

/***************** 오답 학습 요일 *****************/
export const DAY_OF_WEEK = [
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
  'SATURDAY',
  'SUNDAY',
] as const

/***************** 하루치 학습지 진행 *****************/
export const DAILY_WORKSHEET_STATUS = ['NOT_STARTED', 'IN_PROGRESS', 'COMPLETED'] as const

/***************** 직전 학습 대비 점수 변화 *****************/
export const SCORE_CHANGE_TYPE = ['INCREASED', 'DECREASED', 'UNCHANGED'] as const

export const DailyLearningSettingTypeSchema = z.enum(DAILY_LEARNING_SETTING_TYPE)
export type DailyLearningSettingType = z.infer<typeof DailyLearningSettingTypeSchema>

export const DayOfWeekSchema = z.enum(DAY_OF_WEEK)
export type DayOfWeek = z.infer<typeof DayOfWeekSchema>

export const DailyWorksheetStatusSchema = z.enum(DAILY_WORKSHEET_STATUS)
export type DailyWorksheetStatus = z.infer<typeof DailyWorksheetStatusSchema>

export const ScoreChangeTypeSchema = z.enum(SCORE_CHANGE_TYPE)
export type ScoreChangeType = z.infer<typeof ScoreChangeTypeSchema>

/***************** 학생별 사용 여부 *****************/

/** 학원의 재원 학생 한 명과 오늘의 학습 사용 여부. 설정한 적 없는 학생은 서버가 켜진 것으로 낸다 */
export const DailyLearningStudentAccessSchema = z.object({
  studentId: StudentSchema.shape.id,
  name: StudentSchema.shape.name,
  schoolType: StudentSchema.shape.schoolType,
  grade: StudentSchema.shape.grade,
  dailyWorksheetUsed: z.boolean(),
})
export type DailyLearningStudentAccess = z.infer<typeof DailyLearningStudentAccessSchema>

/** allUsed 는 재원 학생이 모두 켜져 있는가다 */
export const DailyLearningAccessSettingsSchema = z.object({
  allUsed: z.boolean(),
  students: z.array(DailyLearningStudentAccessSchema),
})
export type DailyLearningAccessSettings = z.infer<typeof DailyLearningAccessSettingsSchema>

/***************** 출제 설정 *****************/

/** 학교급 하나에서 고른 학기(트라이 키)별 유형 묶음 */
export const DailyLearningCurriculumSchema = z.object({
  schoolType: SchoolTypeSchema,
  trieKeyInfos: z.array(
    z.object({
      trieKey: TrieKeySchema,
      conceptIds: z.array(ConceptSchema.shape.id),
    }),
  ),
})
export type DailyLearningCurriculum = z.infer<typeof DailyLearningCurriculumSchema>

/**
 * 저장된 출제 설정. 저장한 적이 없으면 서버가 curriculums·levelWeight 를 빈 배열로, 나머지를 null 로 낸다.
 * 난이도는 서버가 범위를 검증하지 않지만 선생님 앱의 난이도 선택(1~5)으로만 저장되어 난이도 어휘로 좁힌다.
 * 저장한 설정에 답 형태 제한이 없으면 서버가 네 형태 전체를 채워 낸다.
 */
export const DailyLearningFilterSettingSchema = z.object({
  curriculums: z.array(DailyLearningCurriculumSchema),
  levelWeight: z.array(z.number()),
  problemCount: z.number().nullable(),
  level: ProblemLevelSchema.nullable(),
  answerTypes: z.array(ProblemTypeSchema).nullable(),
  practiceTestOption: PracticeTestOptionSchema.nullable(),
  excludePrevious: z.boolean().nullable(),
  excludeOutOfCurriculum: z.boolean().nullable(),
  equalityLevel: EqualityLevelSchema.nullable(),
  wrongLearningEnabled: z.boolean().nullable(),
  wrongLearningDayOfWeek: DayOfWeekSchema.nullable(),
  wrongLearningRequestType: ProblemRequestTypeSchema.nullable(),
  wrongLearningProblemLimit: z.number().nullable(),
})
export type DailyLearningFilterSetting = z.infer<typeof DailyLearningFilterSettingSchema>

export const DailyLearningProblemCountSchema = z.object({ count: z.number() })
export type DailyLearningProblemCount = z.infer<typeof DailyLearningProblemCountSchema>

/***************** 통계 *****************/

/**
 * 학생의 하루치 오늘의 학습 통계. 학습지가 없거나 아직 풀지 않은 날은 점수·정답 수를 0 으로 채운 NOT_STARTED 다.
 * studyTime 은 분 단위다. 직전 학습이 없으면 scoreDiff 가 null 이다.
 */
export const DailyLearningStatisticSchema = z.object({
  generatedDate: z.iso.date(),
  status: DailyWorksheetStatusSchema,
  score: z.number(),
  answerRate: z.number(),
  correctAnswerCount: z.number(),
  wrongAnswerCount: z.number(),
  totalProblemCount: z.number(),
  scoreDiff: z
    .object({
      scoreDiffRate: z.number(),
      changeType: ScoreChangeTypeSchema,
    })
    .nullable(),
  studentWorksheetId: StudentWorksheetSchema.shape.id.nullable(),
  studyTime: z.number(),
})
export type DailyLearningStatistic = z.infer<typeof DailyLearningStatisticSchema>

/** 학원 학생들의 오늘의 학습 누적 정답·오답 수 순위 한 줄 */
export const DailyLearningRankingSchema = z.object({
  student: z.object({
    studentId: StudentSchema.shape.id,
    name: StudentSchema.shape.name,
    schoolType: StudentSchema.shape.schoolType,
    grade: StudentSchema.shape.grade,
  }),
  totalCorrectAnswerCount: z.number(),
  totalWrongAnswerCount: z.number(),
})
export type DailyLearningRanking = z.infer<typeof DailyLearningRankingSchema>

export const DailyLearningRankingListSchema = z.object({
  rankings: z.array(DailyLearningRankingSchema),
})

/***************** 채점 현황 *****************/

/** 그날 학생 한 명의 오늘의 학습 채점 현황. 학습지가 아직 만들어지지 않았으면 studentWorksheetId·score 가 null 이다 */
export const DailyLearningScoringSchema = z.object({
  studentId: StudentSchema.shape.id,
  studentName: StudentSchema.shape.name,
  studentWorksheetId: StudentWorksheetSchema.shape.id.nullable(),
  assignedProblemCount: z.number(),
  score: z.number().nullable(),
  status: DailyWorksheetStatusSchema,
  scoringList: z.array(
    z.object({
      worksheetProblemId: WorksheetProblemSchema.shape.id,
      result: ScoringResultSchema,
    }),
  ),
})
export type DailyLearningScoring = z.infer<typeof DailyLearningScoringSchema>

/***************** 학생의 주간 오늘의 학습 *****************/
// 학생 홈의 요일별 칸. 학습지가 아직 만들어지지 않은 날은 studentWorksheetId 가 null 이다.
// 2026-10-16 백엔드 develop(b1add2a8c) 의 worksheet/controller/response/WeeklyDailyWorksheetResponse.kt·
// manager/DailyWorksheetReadManager.kt(day = DayOfWeek.name) 로 확인.
export const DailyWorksheetDaySchema = z.object({
  day: DayOfWeekSchema,
  date: z.iso.date(),
  status: DailyWorksheetStatusSchema,
  studentWorksheetId: StudentWorksheetSchema.shape.id.nullable(),
})
export type DailyWorksheetDay = z.infer<typeof DailyWorksheetDaySchema>
